// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { Mint } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Mint';
import { AllocationFactory } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/AllocationFactory';
import {
  RegistrarHoldingOperationsDelegation,
  RegistrarHoldingOperationsDelegation_Mint,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/HoldingOperationsDelegation';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_REQ_MINT, SUC_REQ_MINT } from '../../../../utils/strings';
import { useMintContext } from './context/useMintContext';

interface Args {
  party: string;
  amount: string;
  source: string;
  instrumentId: string;
  reference?: string;
  delegationCid: string;
}

export const useRegistrarDelegatedMint = () => {
  const getMintContext = useMintContext();

  const { submit: createMintRequest } = useExerciseSubmission(
    RegistrarHoldingOperationsDelegation.RegistrarHoldingOperationsDelegation_Mint,
    SUC_REQ_MINT,
    ERR_REQ_MINT,
  );

  const now = new Date();
  return async (args: Args) => {
    const { source, party, amount, instrumentId, reference, delegationCid } = args;

    const mintContext = await getMintContext.mutateAsync({
      registrar: source,
      holder: party,
      instrumentId,
    });

    const mint: Mint = {
      instrumentId: {
        admin: source,
        id: instrumentId,
      },
      amount,
      holder: party,
      reference: reference || '',
      requestedAt: now.toISOString(),
      executeBefore: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days later
      meta: { values: {} },
    };

    const payload: RegistrarHoldingOperationsDelegation_Mint = {
      factoryCid: mintContext.factoryId as ContractId<AllocationFactory>,
      mint,
      mintExtraArgs: {
        context: mintContext.choiceContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };

    const disclosedContracts =
      mintContext.choiceContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await createMintRequest({
      cid: delegationCid as ContractId<RegistrarHoldingOperationsDelegation>,
      argument: payload,
      disclosedContracts,
    });
  };
};
